(() => {
  if (window.__cartProtectionInitialized) return;
  window.__cartProtectionInitialized = true;

  const ROOT = window.Shopify?.routes?.root || '/';

  const SELECTORS = {
    config: '#CartProtectionConfig',
    insuranceToggle: '[data-insurance-toggle]',
    signatureToggle: '[data-signature-toggle]',
    lineItem: 'line-item[data-line-key]',
    removeLink: 'a[data-remove-line]'
  };

  const CART_EVENTS = {
    change: 'cart:change',
    refresh: 'cart:refresh',
    prepareSections: 'cart:prepare-bundled-sections'
  };

  let handlersBound = false;
  let cartListenersBound = false;
  let lastCartSignature = null;
  let mutationQueue = Promise.resolve();
  let shouldReloadCartPage = false;

  function getConfig() {
    const el = document.querySelector(SELECTORS.config);
    if (!el) return null;

    return {
      insuranceAttribute: el.dataset.insuranceAttribute || 'shipping_protection_opt_in',
      insuranceVariantId: Number(el.dataset.insuranceVariantId || 0),
      sectionId: el.dataset.sectionId,
      signatureAttribute: el.dataset.signatureAttribute || 'signature_opt_in',
      signatureVariantId: Number(el.dataset.signatureVariantId || 0),
      stepAmountCents: Number(el.dataset.stepAmountCents || 10000)
    };
  }

  function isCartPage() {
    return window.themeVariables?.settings?.pageType === 'cart';
  }

  function isManagedVariant(variantId, cfg) {
    const normalizedVariantId = Number(variantId);

    return normalizedVariantId === cfg.insuranceVariantId ||
      (cfg.signatureVariantId && normalizedVariantId === cfg.signatureVariantId);
  }

  function getManagedLine(cart, variantId) {
    return cart.items.find((item) => Number(item.variant_id) === Number(variantId));
  }

  function getEligibleSubtotal(cart, cfg) {
    return cart.items.reduce((sum, item) => {
      if (isManagedVariant(item.variant_id, cfg)) return sum;
      return sum + Number(item.final_line_price || 0);
    }, 0);
  }

  function getDesiredInsuranceQty(cart, cfg, insuranceOptIn) {
    if (!insuranceOptIn) return 0;

    const eligibleSubtotal = getEligibleSubtotal(cart, cfg);
    if (eligibleSubtotal <= 0 || cfg.stepAmountCents <= 0) return 0;

    return Math.floor(eligibleSubtotal / cfg.stepAmountCents);
  }

  function getNonManagedCartSignature(cart, cfg) {
    return cart.items
      .filter((item) => !isManagedVariant(item.variant_id, cfg))
      .map((item) => `${item.key}:${item.quantity}`)
      .sort()
      .join('|');
  }

  function getBundledSections() {
    const sections = [];

    document.documentElement.dispatchEvent(new CustomEvent(CART_EVENTS.prepareSections, {
      bubbles: true,
      detail: { sections }
    }));

    return sections;
  }

  async function getCart() {
    const response = await fetch(`${ROOT}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  async function requestCart(endpoint, payload, baseEvent) {
    const sections = getBundledSections();
    const response = await fetch(`${ROOT}${endpoint}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        ...payload,
        sections
      })
    });

    if (!response.ok) {
      throw new Error(`Cart request failed: ${response.status}`);
    }

    const cart = await response.json();
    lastCartSignature = getNonManagedCartSignature(cart, getConfig() || {});
    shouldReloadCartPage = shouldReloadCartPage || isCartPage();

    if (cart.sections) {
      document.documentElement.dispatchEvent(new CustomEvent(CART_EVENTS.change, {
        bubbles: true,
        detail: {
          baseEvent,
          cart
        }
      }));
    } else {
      document.dispatchEvent(new CustomEvent(CART_EVENTS.refresh));
    }

    return cart;
  }

  function enqueueMutation(task) {
    mutationQueue = mutationQueue
      .then(async () => {
        shouldReloadCartPage = false;
        const result = await task();

        if (shouldReloadCartPage) {
          window.location.reload();
        }

        return result;
      })
      .catch((error) => {
        shouldReloadCartPage = false;
        console.error('[cart-protection] mutation failed', error);
      });

    return mutationQueue;
  }

  async function updateCartAttributes(attributes) {
    return requestCart('cart/update.js', { attributes }, 'cart:update');
  }

  async function addVariant(variantId, quantity) {
    if (!variantId || quantity <= 0) return null;

    return requestCart('cart/add.js', {
      items: [{ id: variantId, quantity }]
    }, 'variant:add');
  }

  async function changeLineByKey(lineKey, quantity) {
    if (!lineKey) return null;

    return requestCart('cart/change.js', {
      id: lineKey,
      quantity
    }, 'line-item:change');
  }

  async function syncProtectionForCart(cart, cfg, insuranceOptIn) {
    if (!cfg?.insuranceVariantId) return cart;

    const insuranceLine = getManagedLine(cart, cfg.insuranceVariantId);
    const currentQty = insuranceLine ? Number(insuranceLine.quantity) : 0;
    const desiredQty = getDesiredInsuranceQty(cart, cfg, insuranceOptIn);

    if (desiredQty === currentQty) return cart;

    if (desiredQty === 0) {
      if (!insuranceLine) return cart;
      return changeLineByKey(insuranceLine.key, 0);
    }

    if (!insuranceLine) {
      return addVariant(cfg.insuranceVariantId, desiredQty);
    }

    return changeLineByKey(insuranceLine.key, desiredQty);
  }

  async function removeManagedProducts(cart, cfg) {
    let nextCart = cart;

    const signatureLine = cfg.signatureVariantId ? getManagedLine(nextCart, cfg.signatureVariantId) : null;
    if (signatureLine) {
      nextCart = await changeLineByKey(signatureLine.key, 0) || nextCart;
    }

    const insuranceLine = getManagedLine(nextCart, cfg.insuranceVariantId);
    if (insuranceLine) {
      nextCart = await changeLineByKey(insuranceLine.key, 0) || nextCart;
    }

    return nextCart;
  }

  async function handleInsuranceToggle(input) {
    const cfg = getConfig();
    if (!cfg) return;

    const isOn = !!input.checked;

    await enqueueMutation(async () => {
      let cart = await updateCartAttributes({
        [cfg.insuranceAttribute]: isOn ? '1' : '0',
        [cfg.signatureAttribute]: isOn ? null : '0'
      });

      if (!isOn) {
        await removeManagedProducts(cart, cfg);
        return;
      }

      cart = await getCart();
      await syncProtectionForCart(cart, cfg, true);
    });
  }

  async function handleSignatureToggle(input) {
    const cfg = getConfig();
    if (!cfg?.signatureVariantId) return;

    const checked = !!input.checked;

    await enqueueMutation(async () => {
      let cart = await updateCartAttributes({
        [cfg.signatureAttribute]: checked ? '1' : '0'
      });

      const signatureLine = getManagedLine(cart, cfg.signatureVariantId);

      if (checked && !signatureLine) {
        await addVariant(cfg.signatureVariantId, 1);
        return;
      }

      if (!checked && signatureLine) {
        await changeLineByKey(signatureLine.key, 0);
      }
    });
  }

  function shouldSyncAfterCartChange(cart, cfg) {
    const nextSignature = getNonManagedCartSignature(cart, cfg);
    const shouldSync = lastCartSignature !== null && nextSignature !== lastCartSignature;

    lastCartSignature = nextSignature;
    return shouldSync;
  }

  function bindCartListeners() {
    if (cartListenersBound) return;
    cartListenersBound = true;

    document.addEventListener(CART_EVENTS.change, (event) => {
      const cfg = getConfig();
      if (!cfg?.insuranceVariantId || !event.detail?.cart) return;

      const cart = event.detail.cart;
      if (!shouldSyncAfterCartChange(cart, cfg)) return;

      const toggle = document.querySelector(SELECTORS.insuranceToggle);
      const insuranceOptIn = toggle ? toggle.checked : cart.attributes?.[cfg.insuranceAttribute] !== '0';

      if (!insuranceOptIn) return;

      enqueueMutation(() => syncProtectionForCart(cart, cfg, insuranceOptIn));
    });
  }

  function bindUserIntentHandlers() {
    if (handlersBound) return;
    handlersBound = true;

    document.addEventListener('change', async (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const insuranceToggle = target.closest(SELECTORS.insuranceToggle);
      if (insuranceToggle) {
        event.stopPropagation();
        await handleInsuranceToggle(insuranceToggle);
        return;
      }

      const signatureToggle = target.closest(SELECTORS.signatureToggle);
      if (signatureToggle) {
        event.stopPropagation();
        await handleSignatureToggle(signatureToggle);
      }
    });

    document.addEventListener('click', async (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const removeLink = target.closest(SELECTORS.removeLink);
      if (!removeLink) return;

      const lineItem = removeLink.closest(SELECTORS.lineItem);
      if (!lineItem) return;

      const cfg = getConfig();
      if (!cfg) return;

      if (lineItem.hasAttribute('data-insurance-item')) {
        event.preventDefault();
        event.stopPropagation();

        const toggle = document.querySelector(SELECTORS.insuranceToggle);
        if (toggle) toggle.checked = false;

        await handleInsuranceToggle(toggle || { checked: false });
        return;
      }

      if (lineItem.hasAttribute('data-signature-item')) {
        event.preventDefault();
        event.stopPropagation();

        const signatureToggle = document.querySelector(SELECTORS.signatureToggle);
        if (signatureToggle) signatureToggle.checked = false;

        await handleSignatureToggle(signatureToggle || { checked: false });
      }
    }, true);
  }

  async function init() {
    const cfg = getConfig();
    if (cfg) {
      try {
        const cart = await getCart();
        lastCartSignature = getNonManagedCartSignature(cart, cfg);

         const insuranceOptIn = cart.attributes?.[cfg.insuranceAttribute] !== '0';
         await enqueueMutation(() => syncProtectionForCart(cart, cfg, insuranceOptIn));
      } catch (error) {
        console.error('[cart-protection] failed to initialize cart state', error);
      }
    }

    bindCartListeners();
    bindUserIntentHandlers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
